import React, { useState, useEffect } from 'react';
import { Box, Text, Button, VStack, Select, Input, HStack, Badge } from 'native-base';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import 'firebase/functions';

var POS_OPTIONS = [
  { value: 'square', label: 'Square', provider: 'square', selfServe: true },
  { value: 'doshii', label: 'Doshii', provider: 'doshii' },
  { value: 'lightspeed', label: 'Lightspeed', provider: 'lightspeed' },
  { value: 'swiftpos', label: 'SwiftPOS', provider: 'oolio' },
  { value: 'bepoz', label: 'BePoz', provider: 'oolio' },
  { value: 'tevalis', label: 'Tevalis', provider: 'oolio' },
  { value: 'ordermate', label: 'OrderMate', provider: 'oolio' },
  { value: 'idealpos', label: 'IdealPOS', provider: 'oolio' },
  { value: 'abacus', label: 'Abacus', provider: 'abacus' },
  { value: 'nomni', label: 'Nomni', provider: 'nomni' },
  { value: 'impos', label: 'Impos', provider: 'impos' },
  { value: 'other', label: 'Other', provider: null },
];

/**
 * POS connection form. For Square (direct integration available) it kicks off
 * the self-serve OAuth connect flow. For all other POS systems it saves a
 * request to the requestedPOS collection for the team to action.
 */
function POSRequestForm({ businessId, business }) {
  var [selectedPOS, setSelectedPOS] = useState('');
  var [otherPOS, setOtherPOS] = useState('');
  var [submitting, setSubmitting] = useState(false);
  var [submitted, setSubmitted] = useState(false);
  var [existingRequest, setExistingRequest] = useState(null);
  var [error, setError] = useState(null);

  var squareConnected = business && business.squareConfig;

  useEffect(function () {
    if (!businessId) return;
    var cancelled = false;
    firebase.firestore().collection('requestedPOS')
      .where('businessId', '==', businessId)
      .limit(1)
      .get()
      .then(function (snap) {
        if (!cancelled && !snap.empty) {
          setExistingRequest(snap.docs[0].data());
        }
      })
      .catch(function () { /* silent */ });
    return function () { cancelled = true; };
  }, [businessId]);

  async function handleConnectSquare() {
    setSubmitting(true);
    setError(null);
    try {
      var getAuthUrl = firebase.app()
        .functions('australia-southeast1')
        .httpsCallable('getSquareAuthUrl');
      var result = await getAuthUrl({ businessId: businessId });
      var authUrl = result.data && result.data.authUrl;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setError('Could not start Square connection. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      setError('Could not start Square connection. Please try again.');
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    if (submitting) return;
    if (!selectedPOS) return;
    var posName = selectedPOS === 'other' ? otherPOS.trim() : selectedPOS;
    if (!posName) return;

    setSubmitting(true);
    setError(null);

    try {
      var user = firebase.auth().currentUser;

      var selectedOption = POS_OPTIONS.find(function (o) { return o.value === selectedPOS; });

      var docId = businessId + '_' + selectedPOS;
      await firebase.firestore().collection('requestedPOS').doc(docId).set({
        businessId: businessId,
        bizName: business?.bizName || '',
        bizAddress: business?.bizAddress || '',
        bizSuburb: business?.bizSuburb || '',
        bizState: business?.bizState || business?.bizAddress?.split(',').pop()?.trim() || '',
        bizPostalCode: business?.bizPostalCode || '',
        bizCountry: business?.bizCountry || 'AU',
        posType: selectedPOS,
        posName: posName,
        provider: selectedOption?.provider || null,
        userId: user ? user.uid : null,
        userEmail: user ? user.email : null,
        requestedAt: firebase.firestore.FieldValue.serverTimestamp(),
        status: 'pending',
      });

      setSubmitted(true);
      setExistingRequest({ posType: selectedPOS, posName: posName, status: 'pending' });
    } catch (err) {
      setError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (squareConnected) {
    return (
      <Box bg="green.100" p={5} borderRadius="lg" borderWidth={1} borderColor="green.300" mb={4}>
        <HStack alignItems="center" space={2} mb={2}>
          <Text fontSize="md" fontWeight="semibold" color="green.800">
            Square Connected
          </Text>
          <Badge colorScheme="success" variant="solid">Active</Badge>
        </HStack>
        <Text fontSize="sm" color="coolGray.600">
          Your Square POS{business.squareConfig.locationName ? (' (' + business.squareConfig.locationName + ')') : ''} is connected. Orders placed through Piatto will appear in your Square dashboard.
        </Text>
      </Box>
    );
  }

  if (existingRequest && !submitted) {
    var posLabel = POS_OPTIONS.find(function (o) { return o.value === existingRequest.posType; })?.label
      || existingRequest.posName
      || existingRequest.posType;

    return (
      <Box bg="blue.100" p={5} borderRadius="lg" borderWidth={1} borderColor="blue.300" mb={4}>
        <HStack alignItems="center" space={2} mb={2}>
          <Text fontSize="md" fontWeight="semibold" color="blue.800">
            POS Connection Requested
          </Text>
          <Badge colorScheme="info" variant="solid">Pending</Badge>
        </HStack>
        <Text fontSize="sm" color="coolGray.600">
          You've requested POS connection for <Text fontWeight="bold">{posLabel}</Text>.
          We'll notify you when it's ready.
        </Text>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box bg="green.100" p={5} borderRadius="lg" borderWidth={1} borderColor="green.300" mb={4}>
        <Text fontSize="md" fontWeight="semibold" color="green.800" mb={2}>
          Request Submitted
        </Text>
        <Text fontSize="sm" color="coolGray.600">
          Thanks — we're working to enable your POS. We'll notify you when it's ready.
        </Text>
      </Box>
    );
  }

  return (
    <Box bg="coolGray.100" p={5} borderRadius="lg" borderWidth={1} borderColor="coolGray.200" mb={4}>
      <Text fontSize="md" fontWeight="semibold" mb={2}>
        Connect your POS
      </Text>
      <Text fontSize="sm" color="coolGray.600" mb={4}>
        Connect your point-of-sale system to Piatto to automatically import your menu
        and receive orders directly in your POS. No separate payment setup needed.
      </Text>

      <VStack space={3}>
        <Text fontSize="sm" fontWeight="medium" color="coolGray.700">
          Which POS system does your venue use?
        </Text>

        <Select
          selectedValue={selectedPOS}
          placeholder="Select your POS system"
          onValueChange={function (val) { setSelectedPOS(val); setOtherPOS(''); setError(null); }}
          accessibilityLabel="Select your POS system"
        >
          {POS_OPTIONS.map(function (opt) {
            return (
              <Select.Item key={opt.value} label={opt.label} value={opt.value} />
            );
          })}
        </Select>

        {selectedPOS === 'square' ? (
          <VStack space={3}>
            <Box bg="blue.50" p={3} borderRadius="md" borderWidth={1} borderColor="blue.200">
              <Text fontSize="xs" color="coolGray.700">
                Piatto connects directly to your Square account — importing your menu and
                presenting it accessibly to diners on the Piatto app, with each order
                arriving in Square complete with the customer's accessibility and allergen
                needs. Click below to authorise the connection with Square.
              </Text>
            </Box>

            {error && (
              <Box bg="red.100" p={2} borderRadius="md">
                <Text color="red.700" fontSize="xs">{error}</Text>
              </Box>
            )}

            <Button
              colorScheme="blue"
              onPress={handleConnectSquare}
              isLoading={submitting}
              isDisabled={!businessId}
            >
              Connect Square
            </Button>
          </VStack>
        ) : selectedPOS === 'doshii' ? (
          <VStack space={3}>
            <Box bg="blue.50" p={3} borderRadius="md" borderWidth={1} borderColor="blue.200">
              <Text fontSize="xs" color="coolGray.700">
                Piatto integrates with Doshii to connect your POS and import your menu
                automatically. We'll be in touch to set up your Doshii connection.
              </Text>
            </Box>

            {error && (
              <Box bg="red.100" p={2} borderRadius="md">
                <Text color="red.700" fontSize="xs">{error}</Text>
              </Box>
            )}

            <Button
              colorScheme="blue"
              onPress={handleSubmit}
              isLoading={submitting}
              isDisabled={!businessId}
            >
              Request Doshii Connection
            </Button>
          </VStack>
        ) : (
          <>
            {selectedPOS === 'other' && (
              <Input
                placeholder="Enter POS name (max 30 characters)"
                value={otherPOS}
                onChangeText={function (val) { setOtherPOS(val.substring(0, 30)); }}
                maxLength={30}
                accessibilityLabel="Enter your POS system name"
              />
            )}

            {error && (
              <Box bg="red.100" p={2} borderRadius="md">
                <Text color="red.700" fontSize="xs">{error}</Text>
              </Box>
            )}

            <Button
              colorScheme="blue"
              onPress={handleSubmit}
              isLoading={submitting}
              isDisabled={!selectedPOS || (selectedPOS === 'other' && !otherPOS.trim())}
            >
              Submit Request
            </Button>
          </>
        )}
      </VStack>
    </Box>
  );
}

export default POSRequestForm;
